import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, forkJoin, map, mergeMap, Observable, of, Subscription } from 'rxjs';
import { DeploymentClass } from 'src/app/models/instance-class';
import { ConfigurationsService } from 'src/app/services/api/configurations.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import { Instance } from 'src/app/models/instance/instance';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { ConfigurationApplicationStatus, ConfigurationPackageStatus,
  ConfigurationState } from 'src/app/models/configurations/configuration-state';
import { translate } from '@jsverse/transloco';
import { Constants } from 'src/app/models/constants';
import { InstanceEnvironment } from 'src/app/models/instance-environment';
import { Sort, SortDirection } from 'src/app/models/sort';
import { Configuration } from 'src/app/models/configurations/configuration';
import { KeyValueMap } from 'src/app/models/key-value-map';
import { isDate } from 'src/app/services/util/utils';
import { ConfigurationDetailsComponent } from 'src/app/components/configuration-details/configuration-details.component';
import { MatDialog } from '@angular/material/dialog';
import { CRVTService } from 'src/app/services/api/crvt.service';
import { UrlService } from 'src/app/services/util/url.service';
import { VerifypopupComponent } from 'src/app/components/verifypopup/verifypopup.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { SkiptestPopupComponent } from 'src/app/components/skiptest-popup/skiptest-popup.component';
import { RouterSegments } from 'src/app/models/router-segments';
import { AuthService } from 'src/app/services/auth/auth.service';
import { EnvironmentPaginatorComponent } from '../environment-paginator/environment-paginator.component';
import { EnvironmentTableComponent } from '../environment-table/environment-table.component';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ActivityService } from 'src/app/services/api/activity.service';
type ComparatorFunc = (first: any, second: any) => number;

@Component({
  selector: 'app-environment-release-package',
  templateUrl: './environment-release-package.html',
  styleUrl: './environment-release-package.scss',
  standalone: false
})
export class EnvironmentReleasePackage {
  @ViewChild(EnvironmentPaginatorComponent)paginatorRef: EnvironmentPaginatorComponent;
  @ViewChild(EnvironmentTableComponent) tableRef: EnvironmentTableComponent;
  @Input() data: any;
  @Input() solution: any;
  @Input() solutionVersion: any;
  @Input() getBlueprints: any;
  @Input() isAWS: boolean;
  @Output() updateInstance = new EventEmitter<boolean>();
  private destroyRef = inject(DestroyRef);
  configurations: Configuration[];
  loadDataSubscription: Subscription;
  totalInstance: any;
  availableConfigurations: Configuration[];
  availableConfigLoaded = false;
  allInstances: any;
  instance: Instance;
  releaseManager = false;
  selectedFilter: any =  {
    label: 'All',
    value: ''
  };
  allConfigurationStates: ConfigurationState[] = [];
  configurationStates: ConfigurationState[];
  configuration: Configuration[];
  pageConfigurationStates: ConfigurationState[];
  filteredConfigurationStates: ConfigurationState[];
  configurationsMap: KeyValueMap<string, Configuration>;
  searchTerm: string;
  isReleasePack = false;
  instanceBlueprint: any;
  deployedDate = new Date();
  currentDate = new Date();
  configResultsLoaded = false;
  appliedConfigurationState: ConfigurationState;
  selectedTab = 0;
  filterOptions: any = [
    {
      label: 'All',
      value: ''
    },
    {
      label: 'Success',
      value: ['success']
    },
    {
      label: 'Failed',
      value: ['test_failed', 'configure_failed', 'failed']
    }
  ];
  showResultsPagination: boolean;
  sort: Sort;
  Constants = Constants;
  ConfigurationApplicationStatus = ConfigurationApplicationStatus;
  RouterSegments = RouterSegments;
  isVerifying = false;
  applyConfigInProgress = false;
  skipTest: boolean;
  configPackage: string;
  configStateVerify = false;
  pageIndex = 0;
  paginationSize = 10;
  searchedResult = [];
  viewResultData: any;
  viewResultColumns = [
    {
      key: 'name',
      name: 'CONFIGURATIONS.VIEW_RESULTS.HEADERS.RELEASE_PACKAGE_NAME',
      type: 'text',
      sortable: true,
      visible: true,
      disable: true,
      keyLink: 'navigation'
    },
    {
      key: 'version',
      name: 'CONFIGURATIONS.VIEW_RESULTS.HEADERS.VERSION',
      type: 'text',
      sortable: true,
      visible: true,
      disable: true,
    },
    {
      key: 'applicationStartTime',
      name: 'CONFIGURATIONS.VIEW_RESULTS.HEADERS.START_TIME_UTC',
      type: 'date',
      sortable: true,
      visible: true,
      disable: true,
    },
    {
      key: 'applicationFinishTime',
      name: 'CONFIGURATIONS.VIEW_RESULTS.HEADERS.END_TIME_UTC',
      type: 'date',
      sortable: true,
      visible: true,
      disable: true,
    },
    {
      key: 'duration',
      name: 'CONFIGURATIONS.VIEW_RESULTS.HEADERS.DURATION',
      type: 'duration',
      startKey: 'applicationStartTime',
      endKey: 'applicationFinishTime',
      sortable: true,
      visible: true,
      disable: true,
    },
    {
      key: 'status',
      name: 'CONFIGURATIONS.VIEW_RESULTS.HEADERS.STATUS',
      type: 'status',
      sortable: true,
      visible: true,
      disable: false,
    },
  ];
  readonly WARNING_STATUS =[DeployStatus.FAILED, DeployStatus.UPDATE_FAILED];
  showWarningPopup: boolean;

  constructor(private activatedRoute: ActivatedRoute, private configService: ConfigurationsService,
    private instanceService: InstancesService, private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog, private crvtService: CRVTService, public urlService: UrlService,
    private router: Router, private authService: AuthService, private activityService: ActivityService) {
  }

  ngOnInit(): void {
    this.loadReleaseData(true);
  }

  private loadReleaseData(initialLoad?): void {
    this.availableConfigLoaded = false;
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    const tenantId = this.activatedRoute.snapshot.params.id;
    if (this.loadDataSubscription) {
      this.loadDataSubscription.unsubscribe();
      this.loadDataSubscription = null;
    }
    const instanceObj: any = {
      blueprintId: blueprintId,
      tenantId: tenantId,
    };
    this.instanceService.getInstances(instanceObj)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      this.totalInstance = data;
      this.loadDataSubscription = combineLatest([
        this.loadInstancesWithConfigurationState(blueprintId, this.totalInstance),
        this.configService.getConfigurations(blueprintId)
      ])
      .pipe(
        takeUntilDestroyed(this.destroyRef))
        .subscribe(([instances, configurations]) => {
        const instanceId = this.activatedRoute.snapshot.params.instanceId;
        this.allInstances = instances;
        this.allInstances.data.forEach(instance => {
          if (instance.deploymentClass === DeploymentClass.D) {
            instance.environment = 'DEV';
          } else {
            instance.environment = this.getBlueprints?.pathToLiveConfiguration.find(item => item?.instanceId === instance?.id)?.environment;
          }
        });
        this.instance = this.allInstances.data.find(instance => instance.id === instanceId);
        this.configurations = configurations;
        if (initialLoad) {
          this.loadConfigurations();
        }
        const availableConfigurations: any = this.getAvailableConfigurations(this.instance);
        availableConfigurations.forEach(config => {
          config.status = this.instance?.configurationStates?.find(item => item.configurationId === config.id)?.status;
          config.skipTest = this.instance?.configurationStates?.find(item => item.configurationId === config.id)?.skipTest;
        });
        this.availableConfigurations = availableConfigurations;
        this.availableConfigLoaded = true;
        if(this.WARNING_STATUS.includes(this.instance?.status)){
          forkJoin({showWarning: this.getInstaceActivity(this.instance?.id)}).subscribe(({ showWarning }) => {
            this.showWarningPopup = showWarning;
          });
      }
      });
    });
  }

  private getAvailableConfigurations(instance: Instance): Configuration[] {
    const requiredEnvironment = this.getBlueprints?.pathToLiveConfiguration.find(
      ptl => ptl.environment === instance?.environment && ptl.instanceId === instance.id)?.requires;
      let availableConfigurations = this.configurations;
    // Only configurations which are created before (or equal) latest successfully applised configuration on previous environment
    const prodInstance = this.allInstances?.data?.find(instances => instances.id === this.instance.id);
    if (this.instance?.dataZone === DataZone.LIVE &&
      prodInstance?.configurationStates?.find(config => config.status === ConfigurationApplicationStatus.VERIFIED)) {
      const latestVerifiedInstance = prodInstance.configurationStates
        .find(configurationState => configurationState.status === ConfigurationApplicationStatus.VERIFIED);
      const verifiedInstanceConfig =
        this.getConfigurationById(latestVerifiedInstance.configurationId);
      availableConfigurations = availableConfigurations
        .filter(configuration => configuration.dateCreated === verifiedInstanceConfig.dateCreated);
      } else if((this.releaseManager && (instance?.environment === translate('CONFIGURATIONS.STATE.PREPROD')) && requiredEnvironment)) {
        const requiredInstance = this.allInstances?.data?.find(ptlInstance => ptlInstance.environment === requiredEnvironment);
        if(instance?.environment === translate('CONFIGURATIONS.STATE.PREPROD')) {
          availableConfigurations = [];
          this.configurations.filter((item) => {
            if(instance.latestConfigurationState) {
              const instanceValue = parseInt(instance?.latestConfigurationState?.manifest?.version.split('.').shift());
              const originalValue = parseInt(item.manifest.version.split('.').shift());
              if(instanceValue === originalValue) {
                const patchValue = parseInt(item.manifest.version.split('.').pop(), 10);
                if(patchValue > 0) {
                  availableConfigurations.push(item);
                }
              }
            }
          });
        }
        if (requiredInstance?.configurationStates) {
          const latestSuccessfulStateOfRequiredInstance = requiredInstance.configurationStates
            .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
          if (latestSuccessfulStateOfRequiredInstance) {
            const configAvailable = this.configurations;
            const latestAppliedConfigurationOnRequiredInstance =
              this.getConfigurationById(latestSuccessfulStateOfRequiredInstance.configurationId);
              for(const configStates of requiredInstance.configurationStates) {
                if (configStates.status === ConfigurationApplicationStatus.SUCCESS) {
                  for(const availableConfig of configAvailable) {
                    if(availableConfig.id === configStates.configurationId) {
                      availableConfigurations.push(availableConfig);
                    };
                    availableConfigurations = [...new Set(availableConfigurations)];
                  }
                }
              }
          };
        }
    } else if (requiredEnvironment) {
      const requiredInstance = this.allInstances?.data?.find(ptlInstance => ptlInstance.environment === requiredEnvironment);
      if (requiredInstance?.configurationStates) {
        const latestSuccessfulStateOfRequiredInstance = requiredInstance.configurationStates
          .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
        if (latestSuccessfulStateOfRequiredInstance) {
          const configAvailable = availableConfigurations;
          const latestAppliedConfigurationOnRequiredInstance =
            this.getConfigurationById(latestSuccessfulStateOfRequiredInstance.configurationId);
            availableConfigurations = [];
            for(const configStates of requiredInstance.configurationStates) {
              if (configStates.status === ConfigurationApplicationStatus.SUCCESS) {
                for(const availableConfig of configAvailable) {
                  if(availableConfig.id === configStates.configurationId) {
                    availableConfigurations.push(availableConfig);
                  }
                  availableConfigurations = [...new Set(availableConfigurations)];
                }
              }
            }
          const latestVersion = latestAppliedConfigurationOnRequiredInstance?.manifest?.version;
          if (latestVersion && requiredEnvironment !== InstanceEnvironment.PREPROD) {
    
            const [latestMajor, latestMinor] = latestVersion.split('.').map(num => parseInt(num, 10));
            
            availableConfigurations = this.configurations.filter(item => {
              const [major, minor, patch] = item.manifest.version.split('.').map(num => parseInt(num, 10));
              // version is less than selected major.minor
              const isLowerVersion = major < latestMajor || (major === latestMajor && minor < latestMinor);
              // version belongs to same major.minor (hotfixes)
              const isHotfix = major === latestMajor && minor === latestMinor;
              return isLowerVersion || isHotfix;
            });
          }
          availableConfigurations.sort((a, b) => {
            const asort = a.manifest.version.split('.').map(num => parseInt(num, 10));
            const bsort = b.manifest.version.split('.').map(num => parseInt(num, 10));

            for (let i = 0; i < 3; i++) {
              if (asort[i] > bsort[i]) return 1;
              if (asort[i] < bsort[i]) return -1;
            }
            return 0;
          });
        } else {
          availableConfigurations = [];
        }
      } else {
        availableConfigurations = [];
      }
    }
    if (instance?.configurationStates) {
      const latestAttemptedConfig = instance.configurationStates.map(s => ({ state: s, cfg: this.getConfigurationById(s.configurationId) })).filter(x => !!x.cfg).sort((a, b) => this.compareVersions(b.cfg.version, a.cfg.version));
      const failedStates = [ConfigurationApplicationStatus.CONFIG_FAILED, ConfigurationApplicationStatus.CONFIGURE_FAILED, ConfigurationApplicationStatus.TEST_FAILED, ConfigurationApplicationStatus.FAILED,
          ConfigurationApplicationStatus.VERIFICATION_FAILED, ConfigurationApplicationStatus.VERIFY_CONFIGURED_FAILED];
      const latestSuccess = latestAttemptedConfig.find(x => x.state.status === ConfigurationApplicationStatus.SUCCESS);
            const latestFailed  = latestAttemptedConfig.find(x => failedStates.includes(x.state.status));
        if (latestSuccess) {
          availableConfigurations = availableConfigurations.filter(cfg =>
            this.compareVersions(cfg.version, latestSuccess.cfg.version) > 0
        );
        } else if (latestFailed) { 
          availableConfigurations = availableConfigurations.filter(cfg =>
            this.compareVersions(cfg.version, latestFailed.cfg.version) >= 0
        );
      }
    }

    return availableConfigurations;
  }

  compareVersions(version: string, latestVersion: string): number {
    const version1 = version.split('.').map(num => parseInt(num, 10));
    const version2 = latestVersion.split('.').map(num => parseInt(num, 10));
    for (let i = 0; i < Math.max(version1.length, version2.length); i++) {
      if (version1[i] > version2[i]) return 1;
      if (version1[i] < version2[i]) return -1;
    }
    return 0;
  }


  /**
   * Returns configuration corresponding to given configuration id
   * @param - the configuration id
   */
  private getConfigurationById(configurationId: string): Configuration {
    return this.configurations.find(configuration => configuration.id === configurationId);
  }

  private loadConfigurations(): void {
    const instance = this.allInstances?.data?.find(item => item.id === this.instance.id);
    this.loadAvailableConfigurations(instance, this.instance?.latestConfigurationState?.status);
    if (this.selectedFilter.value) {
      this.configurationStates = this.allConfigurationStates.filter(item => this.selectedFilter.value.includes(item?.status)) || [];
    } else {
      this.configurationStates = this.allConfigurationStates || [];
    }
    this.configurationsMap = this.configurations?.reduce((acc, configuration) => {
      acc[configuration.id] = configuration;
      return acc;
    }, {});
    this.filterRows();
  }

  filterRows(): void {
    // Remove all configuration states
    if (this.filteredConfigurationStates) {
      this.filteredConfigurationStates.splice(0, this.filteredConfigurationStates.length);
    }

    if (this.configurationStates?.length > 0) {
      this.configurationStates.forEach( configState => {
        this.configurations.forEach( config => {
          if(configState.configurationId === config.id){
            if(config?.manifest?.code) {
              configState.codeResult = config.manifest.code;
            }
          }
        });
      });
      this.filteredConfigurationStates = [...this.configurationStates]
      .sort(this.propertyComparator('applicationFinishTime',SortDirection.DESC))
      .filter(configurationState => Constants.CONFIGURATION_FINAL_STATES.includes(configurationState.status));  
    }
    this.modifyConfiguration();
  }

  /**
   * Sorts configuration states
   * @param - the property and direction to sort
   */
  private propertyComparator(property: string, direction: SortDirection): ComparatorFunc {
    return (configurationState1: ConfigurationState, configurationState2: ConfigurationState): number => {
      if (isDate(configurationState1[property])) {
        return direction === SortDirection.ASC
        ? configurationState1[property]?.getTime() - configurationState2[property]?.getTime()
        : configurationState2[property]?.getTime() - configurationState1[property]?.getTime();
      } else {
        if (configurationState1[property] && configurationState2[property]) {
          return direction === SortDirection.ASC
            ? configurationState1[property].localeCompare(configurationState2[property])
            : -(configurationState1[property].localeCompare(configurationState1[property]));
        }
      }
    };
  }

  loadAvailableConfigurations(instance: Instance, configStatus): void {
    this.availableConfigurations = this.getAvailableConfigurations(instance);
    this.isReleasePack = true;
  }


  private loadInstancesWithConfigurationState(blueprintId: string, instances): Observable<Instance[]> {
    const instanceObj: any = {
      blueprintId: blueprintId,
      tenantId: this.activatedRoute.snapshot.params.id,
      size: instances.total === 0 ? undefined : instances.total
    };
    return this.instanceService.getInstances(instanceObj)
      .pipe(mergeMap((instances: any) => {
        const instanceId = this.activatedRoute.snapshot.params.instanceId;
        this.instanceBlueprint = instances;
        const latestConfigRequests = this.instanceBlueprint.data
          .filter(instance => instance.configurationId)
          .map(instance => this.configService.getConfigurationStates(
            instance.id
          ));
        this.deployedDate = instances.data.length > 0 ? instances.data[instances.data.length - 1].dateUpdated : this.currentDate;
        if (latestConfigRequests.length > 0) {
          return combineLatest(latestConfigRequests).pipe(map((configurationStates: any) => {
            if (configurationStates) {
              this.configResultsLoaded = true;
              configurationStates.forEach(configurationStatesForInstance => {
                if (configurationStatesForInstance?.length > 0) {
                  const instance = instances.data.find(inst => configurationStatesForInstance[0].instanceId === inst.id);
                  this.setInstanceConfigurationStates(instance, configurationStatesForInstance);
                }
              });
            }
            return instances;
          }));
        } else {
          this.configResultsLoaded = true;
        }
        return of(instances);
      }
      ));
  }

    /**
   * Sets configuration states for all instances
   * @param - instance and its configuration states
   */
  private setInstanceConfigurationStates(instance: Instance, configurationStates: ConfigurationState[]): void {
    if (instance?.id === this.data?.id) {
      this.allConfigurationStates = configurationStates;
    }
    if (configurationStates.find(config => config.status === ConfigurationApplicationStatus.CONFIGURING ||
      config.status === ConfigurationApplicationStatus.VERIFYING)) {
      this.appliedConfigurationState = configurationStates.find(config => config.status === ConfigurationApplicationStatus.CONFIGURING);
    }
    if (configurationStates.find(config => config.id === this.appliedConfigurationState?.id &&
      config.status === ConfigurationApplicationStatus.SUCCESS)) {
      this.selectedTab = 1;
      this.showResultsPagination = true;
      this.loadConfigurations();
    }
    if (!instance || !configurationStates) {
      return;
    }
    configurationStates.sort((state1, state2) => state2?.applicationStartTime.getTime() - state1?.applicationStartTime.getTime());
    instance.configurationStates = configurationStates;
    if (instance.configurationStates?.length > 0) {
      instance.latestConfigurationState = instance.configurationStates[0];
    }
  }

   /**
   * Calls filter function when search term changes
   * @param - the search term entered
   */
  search(searchTerm?: string): void {
    this.searchTerm = searchTerm;
    this.filterRows();
  }

  public tabChange(event): void {
    this.selectedTab = event;
    if (event === 1) {
      this.searchTerm ="";
      this.showResultsPagination = true;
      this.loadConfigurations();
      this.pageIndex = 0;
      this.paginationSize = 10;
    } else {
      this.showResultsPagination = false;
    }
  }
  /**
   * Sorts configuration states for time related column
   * @param - the direction to sort
   */
  private durationComparator(direction: SortDirection): ComparatorFunc {
    return (configurationState1: ConfigurationState, configurationState2: ConfigurationState): number => {
      if (configurationState1.applicationStartTime && configurationState1.applicationFinishTime
        && configurationState2.applicationStartTime && configurationState2.applicationFinishTime) {
          const duration1 = new Date(configurationState1.applicationFinishTime).getTime()
            - new Date(configurationState1.applicationStartTime).getTime();

          const duration2 = new Date(configurationState2.applicationFinishTime).getTime()
            - new Date(configurationState2.applicationStartTime).getTime();

          return direction === SortDirection.ASC ? duration1 - duration2 : duration2 - duration1;
      }
      return 0;
    };
  }

  /**
   * Redirects to view or download function when configuration name is clicked
   * @param - the selected configuration state
   */
  configurationNameClick(configurationState: any, isRvt?: boolean): void {
    this.dialog.open(ConfigurationDetailsComponent, {
      height: '60vh',
      width: '80vw',
      panelClass: 'flex-dialog',
      data: {
        results: true,
        configurationState,
        configuration: this.configurations.find(item => item.id === configurationState.configurationId),
        isRvt
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      if (data) {
        if (data?.type === 'download') {
          this.downloadTestResults(data?.configuration, data?.resultsReportFile);
        } else if (data?.type === 'view') {
          this.viewTestResults(data?.configuration, data?.resultsReportFile, data?.viewType);
        }
      }
    });
  }

    /**
   * Opens the selected result in separate tab for viewing
   * @param - the selected configuration state and file name
   */
  viewTestResults(configurationState: ConfigurationState, filename: string, type?: string): void {
    this.crvtService.getViewResultsUrl(configurationState.blueprintId, configurationState.instanceId, configurationState.id, filename, configurationState.tenantId, type)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((viewUrl) => {
      this.urlService.openUrlInNewTab(viewUrl);
    });
  }

  /**
   * Downloads the selected result
   * @param - the selected configuration state and file name
   */
  downloadTestResults(configurationState: ConfigurationState, filename: string): void {
    const clientName = this.router.url.split('.')[0].split('/')[3];
    const type = undefined;
    const configuration = this.configurationsMap[configurationState.configurationId];
    this.crvtService.downloadCRVT(configuration.blueprintId, configurationState.instanceId, configurationState.id, filename, clientName, type)
    .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

    /**
   * Filters configuration states when filter options changes
   * @param - the option selected in filter
   */
  filterOptionChange(option): void {
    this.selectedFilter = option;
    if (option.value) {
      this.configurationStates = this.allConfigurationStates.filter(item => option.value.includes(item?.status)) || [];
    } else {
      this.configurationStates = this.allConfigurationStates || [];
    }
    this.filterRows();
  }

  /**
   * Checks for the instance failed status and proceeds based on outcome
   * @param - the selected configuration
   */
  public applyConfigClick(configuration): void {
    if (this.showWarningPopup) {
      this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.UPDATE_BUILD.WARNING_TITLE', {action: translate('INSTANCE_DETAILS.RELEASE_PACKAGE')}),
        message: translate('INSTANCE_DETAILS.UPDATE_BUILD.WARNING_NOTE', {action: translate('INSTANCE_DETAILS.RELEASE_PACKAGE')}),
        confirmButtonText: translate('INSTANCE_DETAILS.UPDATE_BUILD.PROCEED'),
        warning: true
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
          this.applyConfig(configuration);
        }
      })
    } else {
      this.applyConfig(configuration)
    }
  }

  /**
 * 
 * @param instanceId 
 * @returns true if the instance has a latest failed config activity.
 */
  getInstaceActivity(instanceId): Observable<boolean> {
    return this.activityService.getInstanceActivityLogs(instanceId).pipe(takeUntilDestroyed(this.destroyRef),
      map(data => {
        if (!data?.length) {
          return false;
        }
        const latestAction = data.filter(log => log?.status === 'failed' && ((log?.action === 'config' && this.instance?.status === DeployStatus.FAILED) || (log?.action === 'update' && this.instance?.status === DeployStatus.UPDATE_FAILED))).sort((a, b) =>
          new Date(b?.startTime).getTime() -new Date(a?.startTime).getTime())[0];
        return !!latestAction;
      })
    );
  }

  /**
   * Redirects to apply configuration function when configuration name is clicked
   * @param - the selected configuration
   */
  applyConfig(configuration){
    this.configPackage = configuration;
    if (this.instance.dataZone === DataZone.LIVE && !this.configStateVerify &&
      configuration.status !== ConfigurationApplicationStatus.VERIFIED) {
      this.dialog.open(VerifypopupComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog',
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          if (data) {
            const dataVerify = localStorage.getItem('verifyUpdate');
            if(configuration.status === ConfigurationApplicationStatus.VERIFIED) {
              this.configStateVerify = true;
            }
            if (dataVerify === 'true') {
              this.verifyConfiguration(configuration.id, this.instance.id);
            }
            else if(dataVerify === 'false') {
              this.applyConfiguration(configuration.id, this.instance.id, configuration);
            }
          }
      });
    } else {
      this.applyConfiguration(configuration.id, this.instance.id, configuration);
    }
  }

  /**
  * Opens popup for skipping test user input when apply button clicked
  * @param - the configuration id and instance id
  */
  private applyConfiguration(configurationId: string, instanceId: string, config?): void {
    if (this.allInstances?.data?.find(instance => instance.id === instanceId).deploymentClass !== DeploymentClass.A) {
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        autoFocus: false,
        width: '566px',
        data: {
          title: translate(`INSTANCE_DETAILS.APPLY_RELEASE_PACKAGE_TITLE`),
          message: translate(`INSTANCE_DETAILS.APPLY_RELEASE_PACKAGE_SELECTION`),
          confirmButtonText: translate('BUTTONS.CONFIRM'),
          confirmationTitle: translate('INSTANCE_DETAILS.APPLY_RELEASE_PACKAGE_CONFIRMATION'),
          optionSelection: [
            {value: 'now', label: 'Now', selected: true},
             ...(!this.isAWS ? [{ value: 'schedulelater', label: 'Schedule Later', selected: false }] : [])
          ]
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          if (confirm.value === 'schedulelater') {
            localStorage.setItem('scheduleInstance', JSON.stringify([{'instanceType': 'Apply Release Package', 'configPackage': this.configPackage, 'instanceName': this.instance?.name, 'blueprintName': this.getBlueprints?.name}]) );
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , this.data.tenantId, RouterSegments.SCHEDULE, RouterSegments.CREATE, RouterSegments.TYPE]);
          } else {
            this.dialog.open(SkiptestPopupComponent, {
              minHeight: '342px',
              width: '566px',
              panelClass: 'flex-dialog',
            })
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(data => {
              if (data) {
                this.applyConfigInProgress = true;
                data.skipTest = !data.skipTest;
                this.skipTest = data.skipTest;

                this.applyConfigurationBasedOnInstance(configurationId, instanceId, this.skipTest);
              }
            });
          }
        }
      });
    } else {
      this.skipTest = this.allInstances?.data?.find(instance => instance.id === instanceId).deploymentClass === DeploymentClass.A ? true : false;
      this.applyConfigurationBasedOnInstance(configurationId, instanceId, this.skipTest);
    }
  }

  /**
   * Verifies selected configuration
   * @param - the configuration id and instance id
   */
  private verifyConfiguration(configurationId: string, instanceId: string): void {
    this.configService.verifyConfiguration(configurationId, instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isVerifying = true;
        this.loadReleaseData();
      },
      (error) => {
        const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
        this.dialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('NOTIFICATIONS.APPLY_CONFIGURATION_FAILED'),
            description: errorMessage,
          }
        });
      });
  }

    /**
   * Applies selected configuration
   * @param - the configuration id, instance id and skip test value
   */
  private applyConfigurationBasedOnInstance(configurationId, instanceId, skipTest): void {
    this.configService.applyConfiguration(configurationId, instanceId, skipTest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateInstance.emit(this.applyConfigInProgress);
        this.instanceService.triggerRefresh();
        this.loadReleaseData();
      },
      (error) => {
        const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
        this.dialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('NOTIFICATIONS.APPLY_CONFIGURATION_FAILED'),
            description: errorMessage,
          }
        });
      });
  }
    /**
   * Calls filter function when search term changes for components
   * @param - the search term entered
   */
  searchedItems(result): void {
    this.searchedResult = result;
  }
  modifyConfiguration() {
    this.viewResultData = this.filteredConfigurationStates?.map(state => {
      const config = this.configurationsMap?.[state.configurationId];
      const patchValue = parseInt(config?.manifest?.version?.split('.').pop() ?? '0',10);
      let status = "";
      switch (state.status) {
        case ConfigurationApplicationStatus.SUCCESS:
          status = translate('CONFIGURATIONS.VIEW_RESULTS.SUCCESS');
          break;
        case ConfigurationApplicationStatus.VERIFIED:
          status = translate('CONFIGURATIONS.VIEW_RESULTS.VERIFIED');
          break;
        default:
          status = translate('CONFIGURATIONS.VIEW_RESULTS.FAILED');
          break;
      }
      return {
        name: config?.manifest?.name,
        version: config?.manifest?.version,
        applicationStartTime: state.applicationStartTime,
        applicationFinishTime: state.applicationFinishTime,
        status: status,
        hotfix: patchValue > 0,
        codeResult: state.codeResult,
        configurationState: state
      };
    });
    if (this.selectedFilter.value) {
      this.viewResultData = this.viewResultData?.filter(item => this.selectedFilter.value.includes(item?.status.toLowerCase())) || []
    }
  }

 onPageChange(event) {
    this.paginationSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.tableRef?.pageChanged(event);
  }
  onTablePageChanged(event) {
    this.pageIndex = event.pageIndex;
    this.paginationSize = event.pageSize;
  }
  
  resetPaginator() {
    this.pageIndex = 0;
    this.paginatorRef.pageIndex = 0;
  }
}
